import { logger } from '@/lib/api/logger';
import { isShadowMode } from '@/config';
import { shadowRootModePrefix } from '@/config/constants';

class LocalStorageHelper {
  private readonly isLocalStorageSupported: boolean;

  private readonly prefix: string;

  constructor() {
    this.isLocalStorageSupported = getIsLocalStorageSupported();
    this.prefix = isShadowMode ? shadowRootModePrefix : '';
  }

  set<T = string>(key: ULocalStorageKey, value: T): void {
    if (!this.isLocalStorageSupported) return;

    try {
      const stringValue = typeof value === 'string' ? value : JSON.stringify(value);
      window.localStorage.setItem(`${this.prefix}${key}`, stringValue);
    } catch (error) {
      if (error instanceof Error) {
        logger.error('Error setting localStorage item', { error, key, value });
      }
    }
  }

  get<T = string>(key: ULocalStorageKey): T | null {
    if (!this.isLocalStorageSupported) return null;

    try {
      const value = window.localStorage.getItem(`${this.prefix}${key}`);
      return value as T | null;
    } catch (error) {
      if (error instanceof Error) {
        logger.error('Error getting localStorage item', { error, key });
      }
      return null;
    }
  }

  remove(key: ULocalStorageKey): void {
    if (!this.isLocalStorageSupported) return;

    try {
      window.localStorage.removeItem(`${this.prefix}${key}`);
    } catch (error) {
      if (error instanceof Error) {
        logger.error('Error removing localStorage item', { error, key });
      }
    }
  }
}

function getIsLocalStorageSupported(): boolean {
  if (typeof window.localStorage === 'undefined') return false;

  try {
    const testKey = 'test_ls_availability';
    window.localStorage.setItem(testKey, testKey);
    window.localStorage.removeItem(testKey);
    return true;
  } catch (error) {
    if (error instanceof Error) {
      logger.error('Local Storage is not supported in this environment', { error });
    }
    return false;
  }
}

/* Types */
// ? this union autocompletes with defined values and allows arbitrary strings
type ULocalStorageKey = 'search.recentSearchList' | 'color_theme' | (string & {});

export const LS = new LocalStorageHelper();
